// Generated by Copilot
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { POUR_TYPE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PourType } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    pourType: PourType.DetailsResponse;
}

export const PourTypeDetailsCard = ({ pourType }: Props) => {
    const { ...rest } = pourType;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<PourType.UpdateRequest>({
        resolver: yupResolver(POUR_TYPE_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['pourType', pourType.id],
        mutationFn: (data: PourType.UpdateRequest) =>
            Api.Servings.PourType.putPourType(pourType.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: PourType.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated pour type!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error updating pour type!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Pour Type Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            fullWidth
                            name="defaultVolumeMl"
                            label="Volume (ml)"
                            type="number"
                            disabled={!editable}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 10000,
                                    step: 1,
                                },
                            }}
                        />
                        <TextFieldElement
                            fullWidth
                            name="defaultVolumeOz"
                            label="Volume (oz)"
                            type="number"
                            disabled={!editable}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 1000,
                                    step: 0.1,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="PourType">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
